import Stats from "../../Components/Stats";
import React, { useState, useEffect, useRef } from "react";
import { deleteCar, getCarList } from "../../api";
import { Loader } from "../../Components/Loader";
import { MoreVertical, Edit, Trash, Users, Fuel } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import assets from "../../Assets/assets";
import moment from "moment";
import { ToastMsg } from "../../Components/ToastMsg";

const CarListing = () => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const page_size = 10;
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [startDate, setStartDate] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [pageData, setPageData] = useState("");
  const [activeDropdownIndex, setActiveDropdownIndex] = useState(null);

  const abortRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const navigate = useNavigate();

  const fetchCars = async ({
    start = startDate,
    end = endDate,
    isSearch = false,
  }) => {
    try {
      if (isSearch) {
        setLoading(false);
      } else {
        setLoading(true);
      }

      setError("");
      const res = await getCarList({
        search: globalFilter,
        start_date: start,
        end_date: end,
        page: pageIndex,
        page_size,
      });

      setCars(res?.data?.data || []);

      setPageData({
        ...res?.data?.page_data,
        page_size: res?.data?.page_data?.page_size
      });
     
    } catch (e) {
      if (e.name !== "AbortError") {
        setError(e.message || "Failed to load cars");
        setCars([]);
        ToastMsg(e?.response?.data?.message,"error")
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCar = async (id) => {
    try {
      setLoading(true)
      const response = await deleteCar(id);
      ToastMsg(response?.data?.message, "success");
      fetchCars({ start: startDate, end: endDate });
    } catch (err) {
      ToastMsg(err?.response?.data?.message, "error");
    } finally {
      setLoading(false);
    }
  };
  // Debounced effect for search
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      fetchCars({ start: startDate, end: endDate, isSearch: true });
    }, 500);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [globalFilter]);

  // Effect for page change
  useEffect(() => {
    fetchCars({ start: startDate, end: endDate });
  }, [pageIndex]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleStartDate = (date) => {
    setStartDate(moment(date).format("YYYY-MM-DD"));
  };

  const handleEndDate = (date) => {
    setEndDate(moment(date).format("YYYY-MM-DD"));
  };

  const searchCars = () => {
    fetchCars({ search: globalFilter, start: startDate, end: endDate });
  };

  const toggleDropdown = (index) => {
    setActiveDropdownIndex(activeDropdownIndex === index ? null : index);
  };

  if (loading) return <Loader />;

  return (
    <div className="flex flex-col gap-4 p-8 bg-[#161616] min-h-screen">
      {/* Stats with date filter */}
      <Stats
        handleStartDate={handleStartDate}
        handleEndDate={handleEndDate}
        handlegetData={searchCars}
        startDate={startDate}
        endDate={endDate}
        showtiles={false}
        pageData={pageData}
      />

      {/* Search Bar */}
      <div className="flex justify-between items-center">
        <div className="relative search-filter-gradient flex items-center gap-2 w-[378px] h-10 pr-4 pl-4 py-2 rounded-lg">
          <input
            className="bg-transparent outline-none text-[#F6F6F6] text-[12px] w-full placeholder-[#F6F6F6]"
            placeholder="Search cars..."
            value={globalFilter}
            onChange={(e) => {
              setGlobalFilter(e.target.value);
              setPageIndex(1);
            }}
          />
        </div>
        <button
          onClick={() => navigate("/car-listings/add")}
          className="bg-[#F3B753] text-black px-4 py-2 rounded-md flex items-center gap-2 font-medium hover:bg-[#e3a840] transition-all"
        >
          Add Car
        </button>
      </div>

      {/* Cars Grid */}
      {error && <p className="text-red-500">{error}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 relative z-0">
        {cars.map((car, index) => (
          <div
            key={car.id}
            className="bg-[#1E1E1E] rounded-2xl shadow-md relative flex flex-col"
          >
            {/* Image */}
            <img
              src={car.thumbnail_image}
              alt={`${car.brand} ${car.model}`}
              className="h-48 object-cover rounded-2xl m-2"
            />

            {/* Content */}
            <div className="p-4 flex flex-col gap-1 flex-grow">
              <h3 className="text-sm font-semibold">
                {car.brand} {car.model} {car.year}
              </h3>
              <p className="text-white text-xl font-bold mt-1">
                € {car.price_per_day}
                <span className="text-xs font-semibold text-white/70">
                  /day
                </span>
              </p>

              {/* Specs */}
              <div className="mt-2.5 bg-[#2a2a2a] rounded-lg flex justify-between px-4 py-3 text-xs text-gray-200">
                <span className="flex flex-col items-center gap-1">
                  <img src={assets.ManualGearIcon} alt="" className="w-4 h-4" />
                  {car.transmission}
                </span>
                <span className="flex flex-col items-center gap-1">
                  <Users className="w-4 h-4" />
                  {car.seats} Seats
                </span>
                <span className="flex flex-col items-center gap-1">
                  <Fuel className="w-4 h-4" />
                  {car.fuel_type}
                </span>
              </div>

              {/* Footer Actions */}
              <div className="mt-2.5 flex justify-between items-center relative z-10">
                <Link
                  key={car.id}
                  to={`/car-listings/car-details/${car.id}`}
                  className="block text-center bg-[#F3B753] text-black text-sm font-semibold py-2 px-4 w-full rounded-md hover:bg-[#e3a840] transition-all"
                >
                  Select Car
                </Link>

                {/* Dropdown */}
                <div className="relative z-50">
                  <button
                    onClick={() => toggleDropdown(index)}
                    className="rounded hover:bg-[#2c2c2c] transition-all bg-[#2a2a2a] py-2.5 px-2 ms-2"
                  >
                    <MoreVertical className="w-4 h-4 text-white" />
                  </button>

                  {activeDropdownIndex === index && (
                    <div className="absolute right-0 top-10 w-36 bg-[#2c2c2c] rounded-md shadow-lg z-50 overflow-hidden">
                      <button
                        onClick={() =>
                          navigate("/car-listings/edit", {
                            state: { mode: "edit", data: car },
                          })
                        }
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-white hover:bg-[#3a3a3a] transition-all"
                      >
                        <Edit className="w-4 h-4" />
                        Edit Info
                      </button>
                      <div className="w-32 mx-auto border-t border-[#454545]" />
                      <button
                        onClick={() => handleDeleteCar(car?.id)}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-[#FF6B6B] hover:bg-[#3a3a3a] transition-all"
                      >
                        <Trash className="w-4 h-4" />
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pageData?.total_pages > 1 && (
        <div className="flex justify-between items-center text-sm text-[#f6f6f6] mt-4">
          <span className="text-xs font-medium">
            Showing page {(pageData.current_page-1)*pageData.page_size+1 } to {Math.min((pageData.current_page ) * pageData.page_size, pageData.count)} of {pageData.count} 
           
          </span>
          <div className="flex gap-2">
            <button
              disabled={pageIndex === 1}
              onClick={() => setPageIndex(pageIndex - 1)}
              className="border border-[#F6F6F6] text-[#F6F6F6] text-sm px-4 py-3 rounded-lg font-semibold disabled:opacity-50"
            >
              Previous
            </button>
            <button
              disabled={pageIndex === pageData.total_pages}
              onClick={() => setPageIndex(pageIndex + 1)}
              className="border border-[#F6F6F6] text-[#F6F6F6] text-sm px-4 py-3 rounded-lg font-semibold disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarListing;
